import React, { useState } from 'react';
import { ImageSourcePropType, Pressable, StyleSheet, View } from 'react-native';
import IconAvatar from 'shared/components/vxl/avatar/IconAvatar/IconAvatar';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import theme from 'shared/styles/theme';
import { Maybe } from 'shared/utils/types';

export interface SelectableCardSquareProps {
  /** Square shaped icon to be displayed on the card*/
  iconUrl: ImageSourcePropType;
  /** Title to be displayed on the card */
  titleText: string;
  /** Used to display the caption at the bottom of card */
  captionText?: Maybe<string>;
  /** Determines if the card is in a selected state */
  isSelected?: boolean;
  /** Disable the card interaction */
  isDisabled?: boolean;
  /** Function used to handle the card press action */
  onCardPress: () => void;
}

const titleTextLineLimit = 2;
const captionTextLineLimit = 2;

export const SelectableCardSquare = ({
  iconUrl,
  titleText,
  captionText,
  isSelected = false,
  isDisabled = false,
  onCardPress,
}: SelectableCardSquareProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const radioCircleStyle = [
    styles.radioCircle,
    isSelected && styles.radioCircleChecked,
    isDisabled && styles.radioCircleDisabled,
    isPressed && styles.radioCirclePressed,
  ];

  const radioInnerCircleStyle = [
    styles.radioInnerCircleChecked,
    isDisabled && styles.radioInnerCircleDisabled,
  ];

  return (
    <Pressable
      onPress={onCardPress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      role="button"
      accessible
      style={[
        styles.container,
        isFocused && styles.focus,
        isHovered && styles.hover,
        isPressed && styles.press,
        isSelected && styles.selected,
        isDisabled && styles.disabled,
      ]}
      testID={`${titleText} Tile`}
    >
      <View style={styles.mainContainer}>
        <View style={styles.topContainer}>
          <IconAvatar
            variant="MD"
            backgroundColor={theme.color.background.tertiary}
            defaultPlaceholderIcon="STORE"
            imageSource={iconUrl}
            iconAccessibilityLabel="offer-logo-image"
          />

          <View style={radioCircleStyle}>
            {isSelected ? (
              <View style={radioInnerCircleStyle} testID="radio-inner-circle" />
            ) : null}
          </View>
        </View>

        <View>
          <BodyBaseBoldText
            style={(styles.title, isDisabled && styles.disabledTitle)}
            numberOfLines={titleTextLineLimit}
          >
            {titleText}
          </BodyBaseBoldText>
          {captionText ? (
            <BodyXSText
              style={(styles.caption, isDisabled && styles.disabledCaption)}
              numberOfLines={captionTextLineLimit}
            >
              {captionText}
            </BodyXSText>
          ) : null}
        </View>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  caption: {
    color: theme.color.text.secondary,
  },
  container: {
    backgroundColor: theme.color.interactive.secondary,
    borderColor: theme.color.border.primary,
    borderRadius: 4,
    borderWidth: 1,
    flex: 1,
    minHeight: 126,
    minWidth: 167,
    padding: theme.spacing.m,
  },
  disabled: {
    backgroundColor: theme.color.interactive.disabled.background,
    borderColor: theme.color.border.primarySolid,
  },
  disabledCaption: {
    color: theme.color.text.muted,
  },
  disabledTitle: {
    color: theme.color.text.muted,
  },
  focus: {
    backgroundColor: theme.color.interactive.secondary,
  },
  hover: {
    backgroundColor: theme.color.interactive.secondaryHovered,
  },
  mainContainer: {
    flex: 1,
    justifyContent: 'space-between',
  },
  press: {
    backgroundColor: theme.color.interactive.secondaryPressed,
    borderColor: theme.color.border.primary,
  },
  radioCircle: {
    alignItems: 'center',
    borderColor: theme.color.interactive.primary,
    borderRadius: 24,
    borderWidth: 1,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  radioCircleChecked: {
    borderColor: theme.color.interactive.primary,
  },
  radioCircleDisabled: {
    borderColor: theme.color.interactive.disabled.border,
  },
  radioCirclePressed: { backgroundColor: theme.color.background.primary },
  radioInnerCircleChecked: {
    backgroundColor: theme.color.interactive.primary,
    borderRadius: 8,
    height: 16,
    width: 16,
  },
  radioInnerCircleDisabled: {
    backgroundColor: theme.color.interactive.disabled.background,
  },
  selected: {
    borderColor: theme.color.expressive.varoPurple,
  },
  title: {
    color: theme.color.text.primary,
  },
  topContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing.m,
  },
});

export default SelectableCardSquare;
